"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown, Loader2 } from "lucide-react";

export type EmojiSelectOption = {
  value: string;
  label: string;
  emoji?: string;
  icon?: ReactNode;
};

type EmojiSelectProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: EmojiSelectOption[];
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  ariaLabel?: string;
};

export function EmojiSelect({
  id,
  value,
  onChange,
  options,
  disabled = false,
  isLoading = false,
  className = "",
  ariaLabel,
}: EmojiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? options[0],
    [options, value]
  );

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const el = rootRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setOpen(false);
      }
    };

    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, []);

  const handlePick = (next: string) => {
    if (disabled || isLoading) return;
    setOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <div ref={rootRef} className="relative" data-select-root>
      <button
        id={id}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || isLoading}
        onClick={() => setOpen((v) => !v)}
        className={`h-10 w-full appearance-none rounded-md border border-[#2a475e] bg-[#16202d] py-2 pl-3 pr-10 text-sm text-white outline-none focus:border-[#66c0f4] disabled:opacity-40 disabled:cursor-not-allowed text-left flex items-center leading-none ${className}`}
      >
        <span className="inline-flex items-center gap-2 leading-none">
          {selected?.icon}
          <span>{selected?.label}</span>
        </span>
      </button>

      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#8f98a0]">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        )}
      </span>

      {open && !disabled && !isLoading && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          className="win-scrollbar absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-[#2a475e] bg-[#16202d] shadow-lg max-h-[320px] overflow-y-auto overscroll-contain"
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => handlePick(o.value)}
              className={`w-full px-3 py-2 text-left text-sm text-white hover:bg-[#1f2e3f] flex items-center gap-2 ${
                o.value === value ? "bg-[#1f2e3f]" : ""
              }`}
            >
              {o.icon}
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
